/**
 * bpmnShapes - Tradução do node do banco para a notação BPMN desenhada
 *
 * Um mesmo `node_type` pode ter símbolos diferentes conforme o `config`
 * (gateway exclusivo × paralelo × junção, espera por evento × por tempo).
 * Aqui centralizamos essa tradução para paleta, canvas e inspetor falarem
 * exatamente a mesma língua, incluindo a cor e o ícone de cada símbolo.
 *
 * @version 3.0.0
 * RELEASE: 2026-09-04 v3.0.0 - Layout unificado: só Início e Fim continuam
 *          círculo; todos os demais blocos usam o mesmo cartão. Novo mapa
 *          `ICONE_DA_FORMA`.
 * @version 2.1.0
 * RELEASE: 2026-08-31 v2.1.0 - Novo símbolo "Exigência (pré-requisito)".
 * @version 2.0.0
 * RELEASE: 2026-08-31 v2.0.0 - Paleta semântica por tipo de bloco (selo colorido
 *          + ícone), inspirada no construtor do Monday.
 * RELEASE: 2026-08-31 v1.0.0 - Criação inicial (designer BPMN)
 */

import {
  Play,
  Flag,
  Zap,
  UserCheck,
  Filter,
  Split,
  Merge,
  MailQuestion,
  ShieldAlert,
  Workflow as WorkflowIcon,
  type LucideIcon,
} from 'lucide-react';

import type { WorkflowEditorNode, WorkflowNodeType } from '@/hooks/useWorkflowDesigner';

/** Formas BPMN suportadas pelo mapa */
export type BpmnShape =
  | 'evento_inicio'
  | 'evento_intermediario'
  | 'evento_exigencia'
  | 'evento_fim'
  | 'tarefa'
  | 'tarefa_humana'
  | 'subprocesso'
  | 'gateway_exclusivo'
  | 'gateway_paralelo'
  | 'gateway_juncao'
  | 'gateway_condicao';

/** Descobre o símbolo BPMN de um node a partir do tipo + configuração */
export function formaDoNode(
  nodeType: WorkflowNodeType,
  config: Record<string, any> | null | undefined,
): BpmnShape {
  const cfg = config || {};

  switch (nodeType) {
    case 'inicio':
      return 'evento_inicio';
    case 'fim':
      return 'evento_fim';
    case 'espera':
      // Exigência (pré-requisito) é uma espera que bloqueia ações do CRM
      return String(cfg.node_kind || '') === 'exigencia' ? 'evento_exigencia' : 'evento_intermediario';
    case 'tarefa_humana':
      return 'tarefa_humana';
    case 'subworkflow':
      return 'subprocesso';
    case 'condicao':
      return 'gateway_condicao';
    case 'gateway': {
      const kind = String(cfg.gateway_kind || 'exclusivo');
      if (kind === 'paralelo') return 'gateway_paralelo';
      if (kind === 'juncao') return 'gateway_juncao';
      return 'gateway_exclusivo';
    }
    default:
      return 'tarefa';
  }
}

/**
 * Tipo de componente do React Flow usado para cada forma.
 * v3.0.0: apenas Início e Fim continuam como círculo (`bpmnEvent`); todos os
 * demais blocos usam o mesmo cartão (`bpmnCard`).
 */
export function tipoDeComponente(forma: BpmnShape): 'bpmnEvent' | 'bpmnCard' {
  if (forma === 'evento_inicio' || forma === 'evento_fim') return 'bpmnEvent';
  return 'bpmnCard';
}

/** Marcador desenhado dentro do losango do gateway */
export function marcadorDoGateway(forma: BpmnShape): string {
  if (forma === 'gateway_paralelo') return '+';
  if (forma === 'gateway_juncao') return '+';
  if (forma === 'gateway_condicao') return '?';
  return '×';
}

/** Rótulo curto da forma, exibido abaixo/dentro do bloco */
export const ROTULO_DA_FORMA: Record<BpmnShape, string> = {
  evento_inicio: 'Início do processo',
  evento_intermediario: 'Evento / espera',
  evento_exigencia: 'Exigência (pré-requisito)',
  evento_fim: 'Fim',
  tarefa: 'Tarefa automática',
  tarefa_humana: 'Tarefa humana',
  subprocesso: 'Subprocesso',
  gateway_exclusivo: 'Decisão exclusiva',
  gateway_paralelo: 'Execução paralela',
  gateway_juncao: 'Junção (join)',
  gateway_condicao: 'Condição',
};

/** Conveniência: forma direto do registro do editor */
export function formaDoRegistro(node: WorkflowEditorNode): BpmnShape {
  return formaDoNode(node.node_type, node.config);
}

/**
 * Estilo visual de cada símbolo BPMN (tokens semânticos, nunca cor fixa).
 * `selo` colore o quadrado com o ícone; `borda` e `traco` destacam o bloco.
 */
export interface EstiloBpmn {
  /** Classes do selo quadrado com o ícone (fundo + cor do ícone) */
  selo: string;
  /** Classe da cor do texto do tipo do bloco */
  texto: string;
  /** Classe da borda/anel do símbolo */
  borda: string;
}

export const ESTILO_DA_FORMA: Record<BpmnShape, EstiloBpmn> = {
  evento_inicio: { selo: 'bg-success/15 text-success', texto: 'text-success', borda: 'border-success' },
  evento_intermediario: { selo: 'bg-warning/15 text-warning', texto: 'text-warning', borda: 'border-warning' },
  evento_exigencia: { selo: 'bg-destructive/15 text-destructive', texto: 'text-destructive', borda: 'border-destructive' },
  evento_fim: { selo: 'bg-destructive/15 text-destructive', texto: 'text-destructive', borda: 'border-destructive' },
  tarefa: { selo: 'bg-primary/15 text-primary', texto: 'text-primary', borda: 'border-primary' },
  tarefa_humana: { selo: 'bg-accent text-accent-foreground', texto: 'text-foreground', borda: 'border-accent' },
  subprocesso: { selo: 'bg-secondary text-secondary-foreground', texto: 'text-foreground', borda: 'border-secondary' },
  gateway_exclusivo: { selo: 'bg-warning/15 text-warning', texto: 'text-warning', borda: 'border-warning' },
  gateway_paralelo: { selo: 'bg-primary/15 text-primary', texto: 'text-primary', borda: 'border-primary' },
  gateway_juncao: { selo: 'bg-primary/15 text-primary', texto: 'text-primary', borda: 'border-primary' },
  gateway_condicao: { selo: 'bg-warning/15 text-warning', texto: 'text-warning', borda: 'border-warning' },
};

/**
 * Ícone de cada símbolo. v3.0.0: como todos os blocos usam o mesmo cartão, o
 * ícone (com a cor do selo) é a única marca visual do tipo.
 */
export const ICONE_DA_FORMA: Record<BpmnShape, LucideIcon> = {
  evento_inicio: Play,
  evento_intermediario: MailQuestion,
  evento_exigencia: ShieldAlert,
  evento_fim: Flag,
  tarefa: Zap,
  tarefa_humana: UserCheck,
  subprocesso: WorkflowIcon,
  gateway_exclusivo: Filter,
  gateway_paralelo: Split,
  gateway_juncao: Merge,
  gateway_condicao: Filter,
};

/**
 * Dimensão aproximada de cada símbolo, usada pelo dagre na auto-organização.
 * v3.0.0: cartão único para tudo, menos os círculos de Início e Fim.
 */
export function dimensaoDaForma(forma: BpmnShape): { largura: number; altura: number } {
  if (forma === 'evento_inicio' || forma === 'evento_fim') return { largura: 200, altura: 130 };
  return { largura: 300, altura: 170 };
}
